/**
 * Canonical contribution tier labels for table_rates methodology.
 * Used by: calculator (label matching), contribution UI (save labels), backward compat mapping.
 * Single source of truth — add new labels here, not in individual files.
 */
export const TABLE_CONTRIBUTION_LABELS = {
  EMPLOYEE: "Employee",
  SPOUSE: "Spouse",
  ONE_CHILD: "1 Child",
  TWO_CHILDREN: "2 Children",
  THREE_PLUS_CHILDREN: "3+ Children",
  ONE_CHILD_SPOUSE: "1 Child + Spouse",
  TWO_CHILDREN_SPOUSE: "2 Children + Spouse",
  THREE_PLUS_CHILDREN_SPOUSE: "3+ Children + Spouse",
} as const;

export type TableContributionLabel = typeof TABLE_CONTRIBUTION_LABELS[keyof typeof TABLE_CONTRIBUTION_LABELS];

/** Old format labels (pre-2026) for backward compat with legacy contribution data */
export const LEGACY_LABEL_MAP: Record<string, string[]> = {
  [TABLE_CONTRIBUTION_LABELS.EMPLOYEE]: ["Employee"],
  [TABLE_CONTRIBUTION_LABELS.SPOUSE]: ["Employee + Spouse"],
  [TABLE_CONTRIBUTION_LABELS.ONE_CHILD]: ["Employee + 1 Children"],
  [TABLE_CONTRIBUTION_LABELS.TWO_CHILDREN]: ["Employee + 2 Children"],
  [TABLE_CONTRIBUTION_LABELS.THREE_PLUS_CHILDREN]: ["Employee + 3+ Children"],
  [TABLE_CONTRIBUTION_LABELS.ONE_CHILD_SPOUSE]: ["Employee + 1 Children + Spouse"],
  [TABLE_CONTRIBUTION_LABELS.TWO_CHILDREN_SPOUSE]: ["Employee + 2 Children + Spouse"],
  [TABLE_CONTRIBUTION_LABELS.THREE_PLUS_CHILDREN_SPOUSE]: ["Employee + 3+ Children + Spouse"],
};

/** Derive the dependent contribution label from family composition */
export function getDependentContributionLabel(spouseCount: number, childCount: number): string {
  const L = TABLE_CONTRIBUTION_LABELS;
  if (spouseCount > 0 && childCount >= 3) return L.THREE_PLUS_CHILDREN_SPOUSE;
  if (spouseCount > 0 && childCount === 2) return L.TWO_CHILDREN_SPOUSE;
  if (spouseCount > 0 && childCount === 1) return L.ONE_CHILD_SPOUSE;
  if (spouseCount > 0) return L.SPOUSE;
  if (childCount >= 3) return L.THREE_PLUS_CHILDREN;
  if (childCount === 2) return L.TWO_CHILDREN;
  if (childCount === 1) return L.ONE_CHILD;
  return "";
}

/** UI keys → labels mapping for the contribution setup form (table methodology) */
export const TABLE_CONTRIBUTION_FORM_KEYS = [
  { key: "employee", label: TABLE_CONTRIBUTION_LABELS.EMPLOYEE },
  { key: "spouse", label: TABLE_CONTRIBUTION_LABELS.SPOUSE },
  { key: "oneChild", label: TABLE_CONTRIBUTION_LABELS.ONE_CHILD },
  { key: "twoChildren", label: TABLE_CONTRIBUTION_LABELS.TWO_CHILDREN },
  { key: "threeChildren", label: TABLE_CONTRIBUTION_LABELS.THREE_PLUS_CHILDREN },
  { key: "oneChildSpouse", label: TABLE_CONTRIBUTION_LABELS.ONE_CHILD_SPOUSE },
  { key: "twoChildrenSpouse", label: TABLE_CONTRIBUTION_LABELS.TWO_CHILDREN_SPOUSE },
  { key: "threeChildrenSpouse", label: TABLE_CONTRIBUTION_LABELS.THREE_PLUS_CHILDREN_SPOUSE },
] as const;
